import { useCallback, useEffect, useState } from 'react';
import { sdk } from '@/services/sdk';
import { rows } from '@/shared/data';

export const CREDIT_RATE_FIELDS = ['Name', 'rate_c', 'notes_c', 'ModifiedOn'];

/** Starting rates in USD per credit. Used until the Credit rates list has rows. */
export const DEFAULT_CREDIT_RATES = [
  { Name: 'Banshee', rate_c: 2 },
  { Name: 'Wraith', rate_c: 1.8 },
  { Name: 'Demon', rate_c: 1.4 },
];

const key = (name) => String(name ?? '').trim().toLowerCase();

const numOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Every saved rate, by name. Falls back to the starting rates when none are saved. */
export async function loadCreditRates() {
  const res = await sdk.table('credit_rates_c').select(CREDIT_RATE_FIELDS).orderBy('Name').limit(200).fetch();
  const list = rows(res) ?? [];
  return list.length ? list : DEFAULT_CREDIT_RATES;
}

/** Rates for a form or page. Never throws: a failed load leaves the starting rates. */
export function useCreditRates() {
  const [rates, setRates] = useState(DEFAULT_CREDIT_RATES);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      setRates(await loadCreditRates());
    } catch {
      setRates(DEFAULT_CREDIT_RATES);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { rates, loaded, reload };
}

/** The saved rate row for a service name (case does not matter), or null. */
export function findRate(rates, service) {
  const k = key(service);
  if (!k) return null;
  return (rates ?? []).find((r) => key(r.Name) === k) ?? null;
}

export const rateFor = (rates, service) => numOrNull(findRate(rates, service)?.rate_c);

/** Credits x the service's rate, rounded to cents. Null when there is no rate or no credits. */
export function costForCredits(rates, service, creditsUsed) {
  const rate = rateFor(rates, service);
  const n = numOrNull(creditsUsed);
  if (rate === null || n === null) return null;
  return Math.round(rate * n * 100) / 100;
}

/** $2.00, $1.80, $0.125 — enough places to show the whole rate. */
export function rateMoney(rate) {
  const n = numOrNull(rate);
  if (n === null) return '—';
  const cents = Math.round(n * 100) / 100;
  return `$${cents === n ? n.toFixed(2) : String(n)}`;
}

/** "Banshee · $2.00 per credit", or empty when the service has no rate. */
export function rateLabel(rates, service) {
  const row = findRate(rates, service);
  const rate = numOrNull(row?.rate_c);
  return rate === null ? '' : `${row.Name} · ${rateMoney(rate)} per credit`;
}

/** The service an order runs on: its plan, or the product's name for custom products. */
export function serviceOfOrder(order) {
  if (!order) return '';
  if (order.plan_c === 'Product') return order.product_c?.Name ?? '';
  return order.plan_c ?? '';
}
